import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain,
  Lightbulb,
  Target,
  Zap,
  ArrowRight,
  ArrowDown,
  CheckCircle,
  Clock,
  Lock,
  Play,
  Settings,
  Layers,
  GitBranch,
  Award,
  RefreshCw,
  Sparkles,
  Info,
  ChevronRight,
  Star,
  Workflow,
  Puzzle,
  Beaker,
  Rocket
} from 'lucide-react';
import { trizWorkflowNodes, trizPrincipleBlocks, contradictionTypes } from '../../data/trizWorkflowData';
import { WorkflowNode } from '../../types';

const TrizWorkflow = () => {
  const [nodes, setNodes] = useState<WorkflowNode[]>(trizWorkflowNodes);
  const [selectedNode, setSelectedNode] = useState<WorkflowNode | null>(null);
  const [selectedPrinciple, setSelectedPrinciple] = useState<any>(null);
  const [workflowProgress, setWorkflowProgress] = useState<Record<string, boolean>>({
    'problem-identification': false,
    'problem-analysis': false,
    'contradiction-mapping': false,
    'triz-tool-selection': false,
    'inventive-principles': false,
    'patterns-evolution': false,
    'substance-field': false,
    'principle-application': false,
    'evolution-application': false,
    'field-transformation': false,
    'solution-synthesis': false,
    'solution-evaluation': false,
    'implementation-planning': false,
    'innovation-deployment': false
  });
  const [activePhase, setActivePhase] = useState<number>(1);

  // Synchronize node status with workflow progress
  useEffect(() => {
    setNodes(prevNodes => 
      prevNodes.map(node => {
        if (workflowProgress[node.id]) {
          return { ...node, status: 'completed' as const };
        }
        
        // Check if this node should be available
        const hasCompletedPrerequisites = node.connections.length === 0 || 
          prevNodes.some(n => n.connections.includes(node.id) && workflowProgress[n.id]);
        
        if (hasCompletedPrerequisites || node.id === 'problem-identification') {
          return { ...node, status: 'available' as const };
        }
        
        return { ...node, status: 'locked' as const };
      })
    );
  }, [workflowProgress]);

  const getNodeIcon = (type: string) => {
    switch (type) {
      case 'start': return Play;
      case 'process': return Settings;
      case 'ai': return Brain;
      case 'decision': return GitBranch;
      case 'end': return Award;
      default: return Puzzle;
    }
  };

  const getNodeTheme = (type: string, status: string) => {
    if (status === 'completed') {
      return {
        gradient: 'from-emerald-400 via-emerald-500 to-emerald-600',
        shadow: 'shadow-emerald-500/30',
        glow: 'shadow-[0_0_30px_rgba(16,185,129,0.4)]',
        border: 'border-emerald-400/50'
      };
    }
    
    if (status === 'available') {
      switch (type) {
        case 'start':
          return {
            gradient: 'from-blue-400 via-blue-500 to-indigo-600',
            shadow: 'shadow-blue-500/30',
            glow: 'shadow-[0_0_30px_rgba(59,130,246,0.4)]',
            border: 'border-blue-400/50'
          };
        case 'ai':
          return {
            gradient: 'from-purple-400 via-purple-500 to-pink-600',
            shadow: 'shadow-purple-500/30',
            glow: 'shadow-[0_0_30px_rgba(139,92,246,0.4)]',
            border: 'border-purple-400/50'
          };
        case 'decision':
          return {
            gradient: 'from-orange-400 via-orange-500 to-red-600',
            shadow: 'shadow-orange-500/30',
            glow: 'shadow-[0_0_30px_rgba(249,115,22,0.4)]',
            border: 'border-orange-400/50'
          };
        case 'end':
          return {
            gradient: 'from-violet-400 via-violet-500 to-purple-600',
            shadow: 'shadow-violet-500/30',
            glow: 'shadow-[0_0_30px_rgba(139,92,246,0.4)]',
            border: 'border-violet-400/50'
          };
        default:
          return {
            gradient: 'from-cyan-400 via-cyan-500 to-blue-600',
            shadow: 'shadow-cyan-500/30',
            glow: 'shadow-[0_0_30px_rgba(6,182,212,0.4)]',
            border: 'border-cyan-400/50'
          };
      }
    }
    
    return {
      gradient: 'from-slate-400 via-slate-500 to-slate-600',
      shadow: 'shadow-slate-400/20',
      glow: 'shadow-[0_0_15px_rgba(100,116,139,0.2)]',
      border: 'border-slate-400/30'
    };
  };

  const completeNode = (nodeId: string) => {
    setWorkflowProgress(prev => ({
      ...prev,
      [nodeId]: true
    }));

    // Update phase based on completed nodes
    const completedCount = Object.values({...workflowProgress, [nodeId]: true}).filter(Boolean).length;
    if (completedCount <= 3) setActivePhase(1);
    else if (completedCount <= 7) setActivePhase(2);
    else if (completedCount <= 11) setActivePhase(3);
    else if (completedCount <= 13) setActivePhase(4);
    else setActivePhase(5);
  };

  const resetWorkflow = () => {
    setWorkflowProgress(Object.keys(workflowProgress).reduce((acc, key) => ({ ...acc, [key]: false }), {}));
    setSelectedNode(null);
    setActivePhase(1);
  };

  const completedCount = Object.values(workflowProgress).filter(Boolean).length;
  const progressPercentage = (completedCount / nodes.length) * 100;

  const phases = [
    { id: 1, name: 'Problem Definition', icon: Target, color: 'blue' },
    { id: 2, name: 'TRIZ Tool Selection', icon: Brain, color: 'purple' },
    { id: 3, name: 'Solution Development', icon: Lightbulb, color: 'orange' },
    { id: 4, name: 'Solution Evaluation', icon: CheckCircle, color: 'green' },
    { id: 5, name: 'Implementation', icon: Rocket, color: 'violet' }
  ];

  return (
    <div className="h-full bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_25%,rgba(99,102,241,0.1),transparent)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_75%,rgba(168,85,247,0.1),transparent)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.05),transparent)]"></div>
        
        {/* TRIZ Pattern Background */}
        <div className="absolute inset-0 opacity-5">
          <svg width="100%" height="100%">
            <defs>
              <pattern id="trizPattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                <circle cx="50" cy="50" r="1" fill="white" opacity="0.3"/>
                <path d="M30,50 Q50,30 70,50 Q50,70 30,50" stroke="white" strokeWidth="0.5" fill="none" opacity="0.2"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#trizPattern)" />
          </svg>
        </div>
      </div>

      <div className="relative z-20 p-8 h-full overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <div className="p-4 rounded-2xl bg-gradient-to-r from-purple-500/20 to-blue-500/20 backdrop-blur-xl border border-white/10">
                  <Workflow className="w-10 h-10 text-purple-300" />
                </div>
                <div>
                  <h1 className="text-4xl sqsft-heading-1 text-white">
                    TRIZ Problem Solving Workflow
                  </h1>
                  <p className="sqsft-body text-lg" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Systematic innovation methodology with 40 inventive principles
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="bg-black/40 backdrop-blur-2xl rounded-2xl p-6 border border-white/10">
                  <div className="flex items-center space-x-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold sqsft-gradient-text">
                        {completedCount}/{nodes.length}
                      </div>
                      <div className="sqsft-caption uppercase tracking-wider">Steps</div>
                    </div>
                    
                    <div className="w-16 h-16 relative">
                      <svg className="transform -rotate-90 w-16 h-16">
                        <circle
                          cx="32"
                          cy="32"
                          r="28"
                          stroke="currentColor"
                          strokeWidth="3"
                          fill="transparent"
                          className="text-slate-700"
                        />
                        <motion.circle
                          cx="32"
                          cy="32"
                          r="28"
                          stroke="url(#trizProgressGradient)"
                          strokeWidth="3"
                          fill="transparent"
                          strokeLinecap="round"
                          initial={{ pathLength: 0 }}
                          animate={{ pathLength: progressPercentage / 100 }}
                          transition={{ duration: 1, ease: "easeInOut" }}
                          strokeDasharray={176}
                          strokeDashoffset={176}
                        />
                        <defs>
                          <linearGradient id="trizProgressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#8b5cf6" />
                            <stop offset="50%" stopColor="#06b6d4" />
                            <stop offset="100%" stopColor="#10b981" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Brain className="w-5 h-5 text-purple-400" />
                      </div>
                    </div>
                  </div>
                </div>
                
                <motion.button
                  whileHover={{ scale: 1.05, rotate: 180 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={resetWorkflow}
                  className="p-4 rounded-2xl bg-gradient-to-r from-slate-800/80 to-slate-700/80 backdrop-blur-xl border border-white/10 text-slate-300 hover:text-white transition-all duration-300"
                >
                  <RefreshCw className="w-6 h-6" />
                </motion.button>
              </div>
            </div>

            {/* Phase Navigation */}
            <div className="flex items-center space-x-2 mb-8 overflow-x-auto pb-2">
              {phases.map((phase, index) => {
                const Icon = phase.icon;
                const isActive = activePhase === phase.id;
                const isCompleted = activePhase > phase.id;
                
                return (
                  <React.Fragment key={phase.id}>
                    <motion.div
                      className={`flex items-center space-x-3 px-4 py-3 rounded-xl backdrop-blur-xl border transition-all duration-300 ${
                        isActive 
                          ? `bg-${phase.color}-500/20 border-${phase.color}-400/50 shadow-lg` 
                          : isCompleted
                          ? 'bg-emerald-500/20 border-emerald-400/50'
                          : 'bg-slate-800/40 border-slate-600/30'
                      }`}
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className={`p-2 rounded-lg ${
                        isActive 
                          ? `bg-${phase.color}-500` 
                          : isCompleted 
                          ? 'bg-emerald-500' 
                          : 'bg-slate-600'
                      }`}>
                        {isCompleted ? (
                          <CheckCircle className="w-4 h-4 text-white" />
                        ) : (
                          <Icon className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <div>
                        <div className={`text-sm font-medium ${
                          isActive || isCompleted ? 'text-white' : 'text-slate-400'
                        }`}>
                          Phase {phase.id}
                        </div>
                        <div className={`text-xs ${
                          isActive || isCompleted ? 'text-slate-300' : 'text-slate-500'
                        }`}>
                          {phase.name}
                        </div>
                      </div>
                    </motion.div>
                    
                    {index < phases.length - 1 && (
                      <ArrowRight className="w-4 h-4 text-slate-600 flex-shrink-0" />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </motion.div>

          {/* Main Workflow */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Workflow Canvas */}
            <div className="lg:col-span-3">
              <div className="bg-black/20 backdrop-blur-2xl rounded-3xl border border-white/10 p-8 min-h-[600px] relative">
                {/* SVG Connections */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-visible">
                  <defs>
                    <linearGradient id="flowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="rgba(139,92,246,0.6)" />
                      <stop offset="50%" stopColor="rgba(59,130,246,0.8)" />
                      <stop offset="100%" stopColor="rgba(16,185,129,0.6)" />
                    </linearGradient>
                    <filter id="flowGlow">
                      <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                      <feMerge> 
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>
                  
                  {nodes.map(node => (
                    node.connections.map(targetId => {
                      const targetNode = nodes.find(n => n.id === targetId);
                      if (!targetNode) return null;
                      
                      const isActive = workflowProgress[node.id] || workflowProgress[targetId];
                      
                      return (
                        <motion.line
                          key={`${node.id}-${targetId}`}
                          x1={node.position.x + 60}
                          y1={node.position.y + 30}
                          x2={targetNode.position.x + 60}
                          y2={targetNode.position.y + 30}
                          stroke={isActive ? "url(#flowGradient)" : "rgba(100,116,139,0.3)"}
                          strokeWidth={isActive ? "3" : "2"}
                          filter={isActive ? "url(#flowGlow)" : "none"}
                          initial={{ pathLength: 0 }}
                          animate={{ pathLength: 1 }}
                          transition={{ duration: 1.5, delay: 0.3 }}
                          className="transition-all duration-500"
                        />
                      );
                    })
                  )).flat()}
                </svg>

                {/* Workflow Nodes */}
                {nodes.map((node, index) => {
                  const Icon = getNodeIcon(node.type);
                  const theme = getNodeTheme(node.type, node.status);
                  const isCompleted = workflowProgress[node.id];
                  
                  return (
                    <motion.div
                      key={node.id}
                      className="absolute cursor-pointer"
                      style={{
                        left: node.position.x,
                        top: node.position.y,
                      }}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: index * 0.1, type: "spring", bounce: 0.3 }}
                      whileHover={{ scale: node.status !== 'locked' ? 1.05 : 1 }}
                      onClick={() => node.status !== 'locked' && setSelectedNode(node)}
                    >
                      {/* Node Block */}
                      <div className={`relative w-32 h-16 rounded-2xl backdrop-blur-xl border-2 ${theme.border} ${theme.glow} overflow-hidden ${
                        node.status === 'locked' ? 'opacity-50' : ''
                      }`}>
                        {/* Background Gradient */}
                        <div className={`absolute inset-0 bg-gradient-to-r ${theme.gradient} opacity-80`} />
                        
                        {/* Pulse Animation for Available Nodes */}
                        {node.status === 'available' && !isCompleted && (
                          <motion.div
                            className={`absolute inset-0 bg-gradient-to-r ${theme.gradient} opacity-40`}
                            animate={{ 
                              opacity: [0.4, 0.8, 0.4],
                              scale: [1, 1.02, 1]
                            }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                          />
                        )}
                        
                        {/* Content */}
                        <div className="relative z-10 flex items-center justify-between h-full px-3">
                          <div className="flex items-center space-x-2">
                            {node.status === 'locked' ? (
                              <Lock className="w-5 h-5 text-white/70" />
                            ) : isCompleted ? (
                              <CheckCircle className="w-5 h-5 text-white" />
                            ) : (
                              <Icon className="w-5 h-5 text-white" />
                            )}
                            
                            <div className="text-left">
                              <div className="text-xs font-semibold text-white leading-tight">
                                {node.name.split(' ').slice(0, 2).join(' ')}
                              </div>
                              <div className="text-xs font-semibold text-white leading-tight">
                                {node.name.split(' ').slice(2).join(' ')}
                              </div>
                            </div>
                          </div>
                          
                          {node.status === 'available' && !isCompleted && (
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                completeNode(node.id);
                              }}
                              className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm"
                            >
                              <ArrowRight className="w-3 h-3 text-white" />
                            </motion.button>
                          )}
                        </div>
                        
                        {/* Completion Ring */}
                        {isCompleted && (
                          <motion.div
                            className="absolute -inset-1 rounded-2xl border-2 border-emerald-400/60"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.3 }}
                          />
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Side Panel */}
            <div className="space-y-6">
              <AnimatePresence mode="wait">
                {selectedNode ? (
                  <motion.div
                    key="node-details"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="bg-black/40 backdrop-blur-2xl rounded-3xl border border-white/10 p-6"
                  >
                    <div className="space-y-4">
                      <div className="text-center">
                        <div className={`w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br ${getNodeTheme(selectedNode.type, selectedNode.status).gradient} flex items-center justify-center mb-3`}>
                          {React.createElement(getNodeIcon(selectedNode.type), { 
                            className: "w-6 h-6 text-white" 
                          })}
                        </div>
                        <h3 className="sqsft-heading-3 text-white mb-2">
                          {selectedNode.name}
                        </h3>
                        <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          selectedNode.status === 'completed' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' :
                          selectedNode.status === 'available' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' :
                          'bg-slate-500/20 text-slate-300 border border-slate-500/30'
                        }`}>
                          {selectedNode.status === 'completed' ? 'Completed' :
                           selectedNode.status === 'available' ? 'Available' : 'Locked'}
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div>
                          <h4 className="text-sm font-semibold text-white mb-2 flex items-center">
                            <Info className="w-4 h-4 mr-2 text-blue-400" />
                            Description
                          </h4>
                          <p className="text-xs text-slate-300 leading-relaxed">
                            {selectedNode.description}
                          </p>
                        </div>
                        
                        <div>
                          <h4 className="text-sm font-semibold text-white mb-2 flex items-center">
                            <Target className="w-4 h-4 mr-2 text-orange-400" />
                            Details
                          </h4>
                          <p className="text-xs text-slate-300 leading-relaxed">
                            {selectedNode.details}
                          </p>
                        </div>
                        
                        {selectedNode.nextSteps.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-white mb-2 flex items-center">
                              <ChevronRight className="w-4 h-4 mr-2 text-green-400" />
                              Next Steps
                            </h4>
                            <ul className="space-y-1">
                              {selectedNode.nextSteps.map((step, index) => (
                                <li key={index} className="text-xs text-slate-300 flex items-start space-x-2">
                                  <div className="w-1 h-1 bg-green-400 rounded-full mt-1.5 flex-shrink-0" />
                                  <span>{step}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                      
                      {selectedNode.status === 'available' && !workflowProgress[selectedNode.id] && (
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => completeNode(selectedNode.id)}
                          className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium text-sm shadow-lg hover:shadow-blue-500/25 transition-all duration-300 flex items-center justify-center space-x-2"
                        >
                          <Star className="w-4 h-4" />
                          <span>Complete Step</span>
                        </motion.button>
                      )}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="triz-overview"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="bg-black/40 backdrop-blur-2xl rounded-3xl border border-white/10 p-6"
                  >
                    <div className="text-center space-y-4">
                      <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center mb-4">
                        <Brain className="w-8 h-8 text-white" />
                      </div>
                      
                      <div>
                        <h3 className="text-lg font-bold text-white mb-2">
                          TRIZ Methodology
                        </h3>
                        <p className="text-xs text-slate-400">
                          Theory of Inventive Problem Solving
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3">
                          <div className="text-lg font-bold text-purple-400">
                            {completedCount}
                          </div>
                          <div className="text-xs text-purple-300">Completed</div>
                        </div>
                        
                        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                          <div className="text-lg font-bold text-blue-400">
                            {nodes.filter(n => n.status === 'available').length}
                          </div>
                          <div className="text-xs text-blue-300">Available</div>
                        </div>
                      </div>
                      
                      <div className="text-xs text-slate-400 text-left">
                        Click on any available block to explore the TRIZ problem-solving step in detail.
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* TRIZ Principles Panel */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-black/40 backdrop-blur-2xl rounded-3xl border border-white/10 p-6"
              >
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <Sparkles className="w-5 h-5 mr-2 text-yellow-400" />
                  TRIZ Principles
                </h3>
                
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {trizPrincipleBlocks.map((principle, index) => (
                    <motion.div
                      key={principle.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ scale: 1.02 }}
                      onClick={() => setSelectedPrinciple(principle)}
                      className={`p-3 rounded-xl cursor-pointer transition-all duration-300 bg-gradient-to-r ${principle.color}/20 border border-white/10 hover:border-white/20`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-semibold text-white">
                          #{principle.principle} {principle.name}
                        </div>
                        <Beaker className="w-4 h-4 text-white/60" />
                      </div>
                      <div className="text-xs text-slate-300">
                        {principle.description}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrizWorkflow;